import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { actions, Control, LocalForm } from 'react-redux-form';
import { faExclamationTriangle } from '@fortawesome/pro-solid-svg-icons/faExclamationTriangle';

import ModalComponent from 'components/ModalComponent';
import DateSelector from 'components/forms/DateSelector';
import ControlGroup from 'components/forms/ControlGroup';
import ValidationErrors from 'components/forms/ValidationErrors';

import FontAwesome from 'utils/FA';

import { titleize } from 'utils';
import { billingConfigs as billingConfigsDuck, schoolYears as schoolYearsDuck } from '../../../ducks';
import { duckify, singleDuck } from '../../../utils/duckify';
import { ledgerLockCalendarParams, LedgerLockDateLabel } from '../../shared/LedgerLock';
import { formatCurrency } from "utils/i18n";

let formDispatch;

const BulkAddTransaction = ({
  addTransaction,
  billingCategories,
  contactTotal,
  count,
  hideModal,
  schoolYearId,
  search,
  filterContactIds,
  filterType,
  showFlash,
  type,
  jobType,
  schoolYears,
  billingConfig
}) => {
  const [errors, setErrors] = useState({});
  const [disabled, setDisabled] = useState(false);

  const initialState = {
    data: {
      category_id: billingCategories.length > 0 ? billingCategories[0].id : '',
      search: JSON.stringify(search),
      filter_type: filterType,
      filter_contact_ids: JSON.stringify(filterContactIds),
      school_year_id: schoolYearId,
      job_type: jobType || 'contact',
      type
    }
  };

  const handleSubmit = (values) => {
    setDisabled(true);

    addTransaction(values)
      .then(() => hideModal())
      .then(() => showFlash({
        message:
          `A ${type} has been added for the contacts matching this filter. These changes will be reflected shortly.`
      }))
      .catch(({ bulk_add_transaction_job: { errors: { data } } }) => {
        setErrors(data);
        setDisabled(false);
      });
  };

  return (
    <LocalForm
      model='bulk_add_transaction_job'
      initialState={initialState}
      onSubmit={handleSubmit}
      getDispatch={dispatch => { formDispatch = dispatch; }}
    >
      <ModalComponent
        title={`${titleize(type)} Details`}
        buttons={[
          <input
            className='btn btn-primary'
            type='submit'
            value='Submit'
            disabled={disabled}
            onClick={() => formDispatch(
              actions.submit('bulk_add_transaction_job')
            )}
          />
        ]}
      >
        <div className='form-horizontal'>
          <div className='alert alert-warning'>
            <FontAwesome icon={faExclamationTriangle} postNbsp />
            This will add a
            {` ${type} `}
            for
            {` ${count} `}
            selected contacts out of
            {` ${contactTotal} `}
            possible contacts.
          </div>

          <ValidationErrors errors={errors} fieldNames={{ category_id: 'Category' }} />

          <ControlGroup label={<LedgerLockDateLabel billingConfig={billingConfig} />} errors={errors.date} showErrors>
            <DateSelector model='.data.date' controlProps={ledgerLockCalendarParams(billingConfig)} />
          </ControlGroup>

          <ControlGroup label='Category' errors={errors.category_id} showErrors>
            <Control.select className='form-control form-control-sm w-50' model='.data.category_id'>
              {
                billingCategories.map((group) => (
                  <optgroup key={group.id} label={group.name}>
                    <option value={group.id}>{group.name}</option>
                    {
                      group.children.filter(c => c.active).map((category) => (
                        <option key={category.id} value={category.id}>
                          {category.name}
                        </option>
                      ))
                    }
                  </optgroup>
                ))
              }
            </Control.select>
          </ControlGroup>

          <ControlGroup label='Description' errors={errors.description} showErrors>
            <Control.text model='.data.description' placeholder='e.g. Tuition for Student' className='w-50' />
          </ControlGroup>

          {initialState.data.job_type !== 'contact' && (
            <ControlGroup label='Term' errors={errors.school_year_id} showErrors>
              <Control.select className='form-control form-control-sm w-50' model='.data.school_year_id'>
                <option value=''>Select term...</option>
                {schoolYears.filter(schoolYear => schoolYear.billing_enabled).map(schoolYear => (
                  <option value={schoolYear.id} key={schoolYear.id}>{schoolYear.name}</option>
                ))}
              </Control.select>
            </ControlGroup>
          )}

          {initialState.data.job_type === 'contact' && (
            <ControlGroup label='Payers'>
              <div className='text'>Split according to payer responsibility</div>
            </ControlGroup>
          )}

          <ControlGroup label='Amount' errors={errors.amount} showErrors>
            <Control.text model='.data.amount' placeholder={formatCurrency(0)} className='w-50' />
          </ControlGroup>
        </div>
      </ModalComponent>
    </LocalForm>
  );
};

BulkAddTransaction.propTypes = {
  /** Adds a transaction */
  addTransaction: PropTypes.func,

  /** Billing categories a charge/credit can be added to */
  billingCategories: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.string })),

  /** The total number of contacts available for the current search */
  contactTotal: PropTypes.number,

  /** The projected number of contacts affected by this action  */
  count: PropTypes.number,

  /** Hides a modal */
  hideModal: PropTypes.func,

  /** The search criteria for the contacts that we wish to add credits/charges to */
  search: PropTypes.arrayOf(
    PropTypes.shape({
      criterion: PropTypes.string,
      modifier: PropTypes.string,
      value: PropTypes.arrayOf(PropTypes.string)
    })
  ),

  /**
   * A List of Contact IDs to filter search against
   */
  filterContactIds: PropTypes.arrayOf(PropTypes.string),

  /**
   * The type of search filter to apply with filterContactIds. Possible values: default, include, exclude
   *
   * include: apply only to filterContactIds
   * exclude: apply to search result _except_ filterContactIds
   * default: use search result without ID filters
   */
  filterType: PropTypes.string,

  /** Shows a flash message */
  showFlash: PropTypes.func,

  /** The type of transaction to be added */
  type: PropTypes.string,
  schoolYearId: PropTypes.string,
  jobType: PropTypes.string,
  schoolYears: PropTypes.arrayOf(PropTypes.shape({
    billing_enabled: PropTypes.bool,
    id: PropTypes.string,
    name: PropTypes.string,
    review_enabled: PropTypes.bool,
    start_year: PropTypes.number
  })),

  /** billing config for ledger lock */
  billingConfig: PropTypes.shape({
    ledger_lock: PropTypes.string
  })
};

BulkAddTransaction.defaultProps = {
  billingCategories: [],
  contactTotal: 0,
  count: 0,
  schoolYearId: '',
  search: [],
  filterType: '',
  filterContactIds: [],
  type: '',
  addTransaction: () => false,
  hideModal: () => false,
  showFlash: () => false,
  jobType: '',
  schoolYears: [],
  billingConfig: {}
};

const mapDuckToProps = () => ({
  schoolYears: schoolYearsDuck,
  billingConfig: singleDuck(billingConfigsDuck, 'current')
});

export default duckify(mapDuckToProps)(BulkAddTransaction);
